import { Box, Button, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { Grid, GridCellProps, GridColumn, GridToolbar } from '@progress/kendo-react-grid';
import i18next from 'i18next';
import _ from 'lodash';
import React, { Fragment } from 'react';
import { InlineEditingActionsCell } from '../../../../../../Hooks/useKendoGrid';
import useStylesGrid from '../../../../../../styles/grid.styles';
import LoadingIndicator from '../../../../../../ui/old/Feedback/LoadingIndicator';
import useAsiteCustomAttributeTypesQuery from '../../../../hooks/useCodeTables/useAsiteCustomAttributeTypesQuery';
import { useDeelProjectsQuery } from '../../../../hooks/useDGManagement';
import useSecurity from '../../../../hooks/useSecurity/useSecurity';
import useStyles from '../../../../styles/useStyles';
import { EAtributeTypes } from '../../../../types';
import { AttributeCell } from '../../../../ui-kit/AttributeCell';
import { DropDownCell } from '../../../../ui-kit/DropDownCell';
import { usePlaceholderQuery } from '../../hooks/usePlaceholderManagement';
import PathFolderModal from '../PathFolderModal';
import usePlaceholderManagement from './usePlaceholderManagement';

const PlaceholderManagementGrid = () => {
    const { data } = useDeelProjectsQuery();
    const { data: placeholders } = usePlaceholderQuery();
    const classesGrid = useStylesGrid();
    const currentStyle = useStyles();

    const { data: attributeTypes } = useAsiteCustomAttributeTypesQuery();

    const {
        idField,
        editField,
        gridState,
        gridResult,
        handleDataStateChange,
        handleItemChange,
        validate,
        inlineEditing: { addNew, add, discard, enterEdit, update, cancel, remove },
        mutations: {
            createPlaceholderManagementMutation: { isLoading },
        },
    } = usePlaceholderManagement(placeholders);
    const { canManage } = useSecurity();

    return (
        <Fragment>
            {data ? (
                <Fragment>
                    <Grid
                        className={`${classesGrid.grid} ${currentStyle.normalizeColumn}`}
                        style={{
                            marginTop: '1%',
                            marginBottom: '2%',
                        }}
                        {...gridState}
                        {...gridResult}
                        sortable={false}
                        filterable={false}
                        onDataStateChange={handleDataStateChange}
                        dataItemKey={idField}
                        editField={editField}
                        onItemChange={handleItemChange}
                    >
                        <GridToolbar>
                            <Box style={{ minHeight: '30px' }} display="flex" justifyContent="space-between">
                                <Typography variant="h6" style={{ color: 'rgba(0, 0, 0, 0.87)' }}>
                                    Placeholder Management
                                </Typography>
                                {canManage() && (
                                    <Button
                                        onClick={addNew}
                                        variant="contained"
                                        style={{ minWidth: 32, height: 32, padding: 0, borderRadius: '50%' }}
                                    >
                                        <AddIcon style={{ fontSize: 16 }} />
                                    </Button>
                                )}
                            </Box>
                        </GridToolbar>
                        <GridColumn
                            field="targetFolder"
                            title={'Target folder*'}
                            headerClassName={`${classesGrid.gridColumnHeader}  ${classesGrid.defaultCursor}`}
                            className={`${classesGrid.gridColumn}  ${classesGrid.wrapText}`}
                            cell={(props: GridCellProps) => {
                                return (
                                    <td className={`${classesGrid.gridColumn}`}>
                                        <PathFolderModal {...props} disabled={props.dataItem.inEdit ? false : true} />
                                    </td>
                                );
                            }}
                            width={'auto'}
                        />
                        <GridColumn
                            field="deelprojectId"
                            title={'Deelproject*'}
                            headerClassName={`${classesGrid.gridColumnHeader}  ${classesGrid.defaultCursor}`}
                            cell={(props: GridCellProps) => {
                                return (
                                    <td className={`${classesGrid.gridColumn} ${currentStyle.normalizeColumn}`}>
                                        <DropDownCell
                                            {...props}
                                            data={
                                                (_.orderBy(data, [
                                                    (x: any) => x?.name?.toLowerCase(),
                                                ]) as any) ?? []
                                            }
                                            disabled={props.dataItem.inEdit ? false : true}
                                            textField="name"
                                            dataItemKey="id"
                                        />
                                    </td>
                                );
                            }}
                        />
                        <GridColumn
                            field="organisation"
                            title={'Organisation*'}
                            headerClassName={`${classesGrid.gridColumnHeader}  ${classesGrid.defaultCursor}`}
                            cell={(props: GridCellProps) => {
                                return (
                                    <td className={`${classesGrid.gridColumn}`}>
                                        <AttributeCell
                                            {...props}
                                            disabled={props.dataItem.inEdit ? false : true}
                                            idCustomAttribute={EAtributeTypes.Organisation}
                                        />
                                    </td>
                                );
                            }}
                        />
                        <GridColumn
                            field="phase"
                            title={'Fase'}
                            headerClassName={`${classesGrid.gridColumnHeader}  ${classesGrid.defaultCursor}`}
                            cell={(props: GridCellProps) => {
                                return (
                                    <td className={`${classesGrid.gridColumn}`}>
                                        <AttributeCell
                                            {...props}
                                            disabled={props.dataItem.inEdit ? false : true}
                                            textField="description"
                                            dataItemKey="type"
                                            idCustomAttribute={EAtributeTypes.Phase}
                                            hasDeleteOption
                                        />
                                    </td>
                                );
                            }}
                        />
                        <GridColumn
                            field="documentType"
                            title={'DocType'}
                            headerClassName={`${classesGrid.gridColumnHeader}  ${classesGrid.defaultCursor}`}
                            cell={(props: GridCellProps) => {
                                return (
                                    <td className={`${classesGrid.gridColumn}`}>
                                        <AttributeCell
                                            {...props}
                                            disabled={props.dataItem.inEdit ? false : true}
                                            textField="description"
                                            dataItemKey="type"
                                            idCustomAttribute={EAtributeTypes.DocumentType}
                                            hasDeleteOption
                                        />
                                    </td>
                                );
                            }}
                        />
                        <GridColumn
                            field="workpackage"
                            title={'Workpackage'}
                            headerClassName={`${classesGrid.gridColumnHeader}  ${classesGrid.defaultCursor}`}
                            cell={(props: GridCellProps) => {
                                return (
                                    <td className={`${classesGrid.gridColumn}`}>
                                        <AttributeCell
                                            {...props}
                                            disabled={props.dataItem.inEdit ? false : true}
                                            idCustomAttribute={EAtributeTypes.Workpackage}
                                            hasDeleteOption
                                        />
                                    </td>
                                );
                            }}
                        />
                        {canManage() && (
                            <GridColumn
                                title=" "
                                width={'90px'}
                                headerClassName={`${classesGrid.gridColumnHeader}  ${classesGrid.defaultCursor}`}
                                className={classesGrid.gridColumn}
                                cell={(props) => (
                                    <InlineEditingActionsCell
                                        {...props}
                                        edit={enterEdit}
                                        remove={remove}
                                        add={add}
                                        discard={discard}
                                        update={update}
                                        cancel={cancel}
                                        idField={idField}
                                        editField={editField}
                                        validate={validate}
                                        isLoading={isLoading}
                                        confirmationMessage={'Are you sure you want to delete this item?'}
                                        withoutHeaderStyle
                                    />
                                )}
                            />
                        )}
                    </Grid>
                </Fragment>
            ) : (
                <LoadingIndicator />
            )}
        </Fragment>
    );
};

export default PlaceholderManagementGrid;
